import React from 'react';

/**
 * Modal som visar detaljerad information om en leverantör
 * Desktop: centrerad modal, Mobil: slide-up bottom-sheet
 */
const SupplierInfoModal = ({
  isOpen,
  onClose,
  supplier,
  onContact
}) => {
  if (!isOpen || !supplier) return null;

  const getDetailedDescription = () => {
    const generalDiscount = supplier.discountDescription
      ? `${supplier.discountRate}% rabatt på ${supplier.discountDescription}`
      : `${supplier.discountRate}% rabatt på hela deras sortiment`;

    switch(supplier.supplierType?.toLowerCase() || '') {
      case 'kök':
      case 'köksleverantör':
        return `${supplier.companyName} är en ledande leverantör av kvalitetskök. Med fokus på både design och funktionalitet erbjuder de skräddarsydda lösningar för moderna hem. Genom Leadme får du ${generalDiscount}, vilket inkluderar skåp, bänkskivor och kökstillbehör.`;
      case 'badrum':
      case 'badrumsinredning':
        return `${supplier.companyName} specialiserar sig på högkvalitativa badrumslösningar med fokus på både estetik och praktisk funktionalitet. Som Leadme-kund får du ${generalDiscount}.`;
      case 'golv':
      case 'golvleverantör':
        return `${supplier.companyName} är experter på golvlösningar för alla typer av rum och miljöer. Genom Leadme får du ${generalDiscount}.`;
      case 'vitvaror':
      case 'vitvaru':
        return `${supplier.companyName} tillhandahåller vitvaror av högsta kvalitet från ledande varumärken. Tack vare samarbetet med Leadme får du ${generalDiscount}.`;
      case 'belysning':
        return `${supplier.companyName} erbjuder moderna belysningslösningar för alla delar av hemmet. Som Leadme-kund får du ${generalDiscount}.`;
      case 'möbler':
      case 'möbel':
        return `${supplier.companyName} tillhandahåller stilrena möbler för hela hemmet. Genom Leadme får du ${generalDiscount}.`;
      case 'bygg':
      case 'byggvaror':
      case 'byggmaterial':
        return `${supplier.companyName} är en pålitlig leverantör av byggmaterial och byggprodukter av hög kvalitet. Som Leadme-kund får du ${generalDiscount}.`;
      case 'färg':
      case 'måleri':
        return `${supplier.companyName} är specialister på färg och målerivaror. Genom samarbetet med Leadme får du ${generalDiscount}.`;
      case 'inredning':
        return `${supplier.companyName} erbjuder inredningsdetaljer och accessoarer för att sätta den personliga prägeln på ditt hem. Som Leadme-kund får du ${generalDiscount}.`;
      default:
        return `${supplier.companyName} erbjuder produkter och tjänster av hög kvalitet inom sin bransch. Som Leadme-kund får du tillgång till exklusiva rabatter: ${generalDiscount}.`;
    }
  };

  const getContactMethodText = () => {
    switch(supplier.contactMethod || 'direct') {
      case 'direct':
        return `Kontakta ${supplier.companyName} direkt och uppge att du är kund via Leadme för att få din rabatt.`;
      case 'leadme':
        // Förfrågan går till projektets inredningsarkitekt — inte till byggkonsulten
        return `Skicka en förfrågan till din inredningsarkitekt på Leadme, som hjälper dig att komma i kontakt med leverantören.`;
      case 'affiliate':
        return `Besök leverantörens hemsida direkt via knappen nedan för att ta del av erbjudandet.`;
      default:
        return `Använd knappen nedan för att kontakta leverantören.`;
    }
  };

  // Samma formuleringar som knapparna i erbjudandelistan, så kunden känner igen sig
  const getContactButtonText = () => {
    switch(supplier.contactMethod || 'direct') {
      case 'direct':
        return 'Kontakta direkt';
      case 'leadme':
        return 'Kontakta';
      case 'affiliate':
        return 'Besök hemsida';
      default:
        return 'Kontakta';
    }
  };

  const handleContactClick = () => {
    onClose();
    if (typeof onContact === 'function') {
      setTimeout(() => {
        onContact(supplier);
      }, 100);
    } else if (supplier.contactMethod === 'affiliate' && supplier.affiliateUrl) {
      window.open(supplier.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {/* Desktop: centrerad modal */}
      <div className="hidden sm:flex fixed inset-0 bg-black/50 items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-2xl w-full max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="relative bg-gray-50 p-5 flex items-center gap-4 border-b border-gray-200 flex-shrink-0">
            {supplier.avatar ? (
              <img
                src={supplier.avatar}
                alt={supplier.companyName}
                className="w-16 h-16 object-contain rounded-lg"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-leadme-pink-light flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-semibold text-leadme-pink-dark">
                  {supplier.companyName.charAt(0)}
                </span>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-900 truncate">{supplier.companyName}</h2>
              <p className="text-sm text-gray-500">{supplier.supplierType}</p>
              {supplier.discountRate > 0 && (
                <span className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                  {supplier.discountRate}% rabatt
                  {supplier.discountDescription && (
                    <span className="font-normal text-green-600">({supplier.discountDescription})</span>
                  )}
                </span>
              )}
            </div>

            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="p-5 overflow-y-auto flex-1 space-y-5">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Om {supplier.companyName}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {supplier.description || getDetailedDescription()}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Erbjudande via Leadme</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Som kund hos Leadme får du {supplier.discountRate}% rabatt
                {supplier.discountDescription ? ` på ${supplier.discountDescription} ` : ' på sortimentet '}
                hos {supplier.companyName}.
              </p>

              {supplier.discounts && supplier.discounts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {supplier.discounts.map((discount, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
                      <span className="font-medium">{discount.rate}%</span>
                      <span className="ml-1">på {discount.category}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Kontakt</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {getContactMethodText()}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-5 py-3 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100 transition-colors"
            >
              Stäng
            </button>
            <button
              onClick={handleContactClick}
              className="px-4 py-2 bg-leadme-pink text-white text-sm rounded-lg hover:bg-leadme-pink-dark transition-colors"
            >
              {getContactButtonText()}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: slide-up bottom-sheet */}
      <div className="sm:hidden fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
        <div
          className="bg-white rounded-t-2xl shadow-xl w-full max-h-[90vh] flex flex-col overflow-hidden"
          style={{ animation: 'slideUp 0.3s ease-out' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {supplier.avatar ? (
                <img
                  src={supplier.avatar}
                  alt={supplier.companyName}
                  className="w-10 h-10 object-contain rounded-lg flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-leadme-pink-light flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-semibold text-leadme-pink-dark">
                    {supplier.companyName.charAt(0)}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-gray-900 truncate">{supplier.companyName}</h2>
                <p className="text-xs text-gray-500">{supplier.supplierType}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 flex-shrink-0 ml-2"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="px-4 py-3 overflow-y-auto flex-1 space-y-3">
            {/* Discount badge */}
            {supplier.discountRate > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                  {supplier.discountRate}% rabatt
                  {supplier.discountDescription && (
                    <span className="font-normal text-green-600">&middot; {supplier.discountDescription}</span>
                  )}
                </span>
                {supplier.discounts && supplier.discounts.map((discount, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px]">
                    <span className="font-medium">{discount.rate}%</span>
                    <span className="ml-1">{discount.category}</span>
                  </span>
                ))}
              </div>
            )}

            <div>
              <h3 className="text-xs font-semibold text-gray-900 mb-1">Om {supplier.companyName}</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {supplier.description || getDetailedDescription()}
              </p>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-900 mb-1">Kontakt</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {getContactMethodText()}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 flex gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Stäng
            </button>
            <button
              onClick={handleContactClick}
              className="flex-1 py-2.5 text-sm font-medium text-white bg-leadme-pink rounded-lg hover:bg-leadme-pink-dark transition-colors"
            >
              {getContactButtonText()}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplierInfoModal;
